import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Sun, Moon, Coffee, BookOpen, Bed } from 'lucide-react';
import { useTranslation } from '../../contexts/TranslationContext';

interface TimeEvent {
  id: string;
  time: string;
  activity: string;
  icon: string;
  period: 'morning' | 'afternoon' | 'evening' | 'night';
}

interface TimeSequenceConfig {
  events: TimeEvent[];
  showTime: boolean;
  showPeriod: boolean;
  allowReordering: boolean;
  theme: 'primary' | 'secondary' | 'success' | 'warning';
  question?: string;
}

interface TimeSequenceComponentProps {
  resource: {
    id?: string;
    type: string;
    name?: string;
    description?: string;
    config?: TimeSequenceConfig;
    events?: TimeEvent[];
    showTime?: boolean;
    showPeriod?: boolean;
    allowReordering?: boolean;
    theme?: 'primary' | 'secondary' | 'success' | 'warning';
    question?: string;
    [key: string]: any;
  };
}

const getConfigValue = (resource: any, key: string, defaultValue: any) => {
  return resource.config?.[key] ?? resource[key] ?? defaultValue;
};

const getIconComponent = (iconName: string) => {
  const iconMap: { [key: string]: React.ComponentType<any> } = {
    clock: Clock,
    sun: Sun,
    moon: Moon,
    coffee: Coffee,
    book: BookOpen,
    bed: Bed,
  };
  return iconMap[iconName] || Clock;
};

const getPeriodColor = (period: string) => {
  const colors = {
    morning: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    afternoon: 'bg-orange-100 text-orange-800 border-orange-200',
    evening: 'bg-pink-100 text-pink-800 border-pink-200',
    night: 'bg-blue-100 text-blue-800 border-blue-200',
  };
  return colors[period as keyof typeof colors] || colors.morning;
};

const getThemeColors = (theme: string) => {
  const themes = {
    primary: 'bg-blue-500 text-white',
    secondary: 'bg-gray-500 text-white',
    success: 'bg-green-500 text-white',
    warning: 'bg-yellow-500 text-white',
  };
  return themes[theme as keyof typeof themes] || themes.primary;
};

export const TimeSequenceComponent: React.FC<TimeSequenceComponentProps> = ({ resource }) => {
  const { t } = useTranslation();
  const [events, setEvents] = useState<TimeEvent[]>(() => {
    const configEvents = getConfigValue(resource, 'events', []);
    const defaultEvents: TimeEvent[] = [
      { id: '1', time: '07:00', activity: t('timeSequence.wakeUp'), icon: 'sun', period: 'morning' },
      { id: '2', time: '08:00', activity: t('timeSequence.breakfast'), icon: 'coffee', period: 'morning' },
      { id: '3', time: '09:00', activity: t('timeSequence.goToSchool'), icon: 'book', period: 'morning' },
      { id: '4', time: '12:00', activity: t('timeSequence.lunch'), icon: 'coffee', period: 'afternoon' },
      { id: '5', time: '15:00', activity: t('timeSequence.returnFromSchool'), icon: 'book', period: 'afternoon' },
      { id: '6', time: '20:00', activity: t('timeSequence.goToBed'), icon: 'bed', period: 'night' },
    ];
    return configEvents.length > 0 ? configEvents : defaultEvents;
  });

  const [selectedEvent, setSelectedEvent] = useState<string | null>(null);
  const [draggedEvent, setDraggedEvent] = useState<string | null>(null);

  const showTime = getConfigValue(resource, 'showTime', true);
  const showPeriod = getConfigValue(resource, 'showPeriod', true);
  const allowReordering = getConfigValue(resource, 'allowReordering', true);
  const theme = getConfigValue(resource, 'theme', 'primary');
  const question = getConfigValue(resource, 'question', t('timeSequence.defaultQuestion'));

  const handleDragStart = useCallback((eventId: string) => {
    setDraggedEvent(eventId);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedEvent(null);
  }, []);

  const handleDrop = useCallback((targetId: string) => {
    if (!draggedEvent || draggedEvent === targetId || !allowReordering) return;

    setEvents(prevEvents => {
      const draggedIndex = prevEvents.findIndex((e: any) => e.id === draggedEvent);
      const targetIndex = prevEvents.findIndex((e: any) => e.id === targetId);
      
      if (draggedIndex === -1 || targetIndex === -1) return prevEvents;

      const newEvents = [...prevEvents];
      const [draggedItem] = newEvents.splice(draggedIndex, 1);
      newEvents.splice(targetIndex, 0, draggedItem);
      
      return newEvents;
    });
  }, [draggedEvent, allowReordering]);

  const handleEventClick = useCallback((eventId: string) => {
    setSelectedEvent(selectedEvent === eventId ? null : eventId);
  }, [selectedEvent]);

  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">{resource.name}</h3>
        <p className="text-gray-600 mb-4">{resource.description}</p>
        {question && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-blue-800 font-medium">{question}</p>
          </div>
        )}
      </div>

      <div className="space-y-3">
        <AnimatePresence>
          {events.map((event: any, index: number) => {
            const IconComponent = getIconComponent(event.icon);
            const isSelected = selectedEvent === event.id;
            const isDragging = draggedEvent === event.id;

            return (
              <motion.div
                key={event.id}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ 
                  opacity: isDragging ? 0.5 : 1, 
                  y: 0,
                  scale: isSelected ? 1.02 : 1
                }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2 }}
                className={`
                  relative p-4 border-2 rounded-lg cursor-pointer transition-all duration-200
                  ${isSelected ? 'border-blue-400 shadow-lg' : 'border-gray-200 hover:border-gray-300'}
                  ${isDragging ? 'opacity-50' : ''}
                `}
                onClick={() => handleEventClick(event.id)}
                draggable={allowReordering}
                onDragStart={() => handleDragStart(event.id)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleDrop(event.id)}
              >
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getThemeColors(theme)}`}>
                      <IconComponent className="w-5 h-5" />
                    </div>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      {showTime && (
                        <span className="text-sm font-medium text-gray-700 bg-gray-100 px-2 py-1 rounded">
                          {event.time}
                        </span>
                      )}
                      {showPeriod && (
                        <span className={`text-xs px-2 py-1 rounded-full border ${getPeriodColor(event.period)}`}>
                          {event.period === 'morning' && t('timeSequence.morning')}
                          {event.period === 'afternoon' && t('timeSequence.afternoon')}
                          {event.period === 'evening' && t('timeSequence.evening')}
                          {event.period === 'night' && t('timeSequence.night')}
                        </span>
                      )}
                    </div>
                    <p className="text-gray-800 font-medium">{event.activity}</p>
                  </div>

                  {allowReordering && (
                    <div className="flex-shrink-0">
                      <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
                        <span className="text-xs text-gray-500 font-medium">{index + 1}</span>
                      </div>
                    </div>
                  )}
                </div>

                {isSelected && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-3 pt-3 border-t border-gray-200"
                  >
                    <p className="text-sm text-gray-600">
                      <strong>{t('timeSequence.description')}:</strong> {t('timeSequence.activityTakesPlace')} {event.period === 'morning' ? t('timeSequence.inTheMorning') : 
                        event.period === 'afternoon' ? t('timeSequence.inTheAfternoon') : 
                        event.period === 'evening' ? t('timeSequence.inTheEvening') : t('timeSequence.atNight')} {t('timeSequence.at')} {event.time}.
                    </p>
                  </motion.div>
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {allowReordering && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-600">
            💡 <strong>{t('timeSequence.tip')}:</strong> {t('timeSequence.tipDescription')}
          </p>
        </div>
      )}
    </div>
  );
};
